import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Select from 'react-select';

import { useTax1099Api } from '../../resources/hooks/api/useTax1099ApiHook';
import { usePayerData } from '../../resources/hooks/usePayerDataHook';
import { getBranch } from '../../store/user/selectors';
import { US_STATES } from '../../resources/constants';
import { getEligibleCount, getPayerSettings, resendW9, syncTimeLabel } from '../../resources/api/tax1099';
import type { PayerW9Sync, SyncStatus } from '../../resources/api/tax1099';

const STATE_OPTIONS = US_STATES.map((state) => ({
  value: state.code,
  label: `${state.code} — ${state.name}`,
}));

const BADGE_STYLE: Record<SyncStatus, React.CSSProperties> = {
  PENDING: { backgroundColor: '#fff3cd', color: '#856404' },
  SYNCED: { backgroundColor: '#d4edda', color: '#155724' },
  FAILED: { backgroundColor: '#f8d7da', color: '#721c24' },
  SKIPPED: { backgroundColor: '#e2e3e5', color: '#383d41' },
};

const SYNC_STATUS_LABEL: Record<SyncStatus, string> = {
  PENDING: 'Queued',
  SYNCED: 'Sent to Invoices',
  FAILED: 'Failed',
  SKIPPED: 'Skipped',
};

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 20;

/**
 * Payer identity for this branch: one row per (user, branch), matching
 * tax_1099_payer_settings. Every generated 1099 snapshots this at compile
 * time, so it must be saved before the first batch generate.
 *
 * The EIN is write-only: gl-api never returns it, only a masked tail, so a
 * saved EIN is shown masked and only overwritten if the field is retyped.
 */

interface StatePayerId {
  code: string;
  id: string;
}

interface PayerFormValues {
  payer_legal_name: string;
  payer_dba_name: string;
  payer_tin_type: string;
  payer_ein: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  phone: string;
  transmitter_control_code: string;
  apportion_unallocated_income_to_home_state: boolean;
}

const EMPTY_FORM: PayerFormValues = {
  payer_legal_name: '',
  payer_dba_name: '',
  payer_tin_type: '1',
  payer_ein: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  phone: '',
  transmitter_control_code: '',
  apportion_unallocated_income_to_home_state: false,
};

const Tax1099PayerSettings: React.FC = () => {
  const { t, i18n } = useTranslation('Tax1099PayerSettings');
  const branchData: any = useSelector(getBranch);
  const branchId = branchData?.id ?? null;

  const { savePayerSettings, uploadPayerW9, downloadPayerW9Pdf } = useTax1099Api();
  const {
    payerData, isConfigured, loading: payerLoading, loadFailed, refresh: refreshPayerData,
  } = usePayerData();
  const loading = payerLoading || (Boolean(branchId) && payerData === null && !loadFailed);
  const [saving, setSaving] = useState(false);
  // A W-9 upload refreshes the shared payer data, which re-runs the form fill below. The fields read off the
  // W-9 are not saved yet, so that one refill must leave the form alone.
  const keepFormOnRefresh = useRef(false);
  const [existing, setExisting] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [maskedEin, setMaskedEin] = useState('');
  const [values, setValues] = useState<PayerFormValues>(EMPTY_FORM);
  const [statePayerIds, setStatePayerIds] = useState<StatePayerId[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Company W-9 State
  const [w9FileId, setW9FileId] = useState<number | null>(null);
  const [w9FileName, setW9FileName] = useState<string>('');
  const [w9Uploading, setW9Uploading] = useState<boolean>(false);
  const [w9Downloading, setW9Downloading] = useState<boolean>(false);
  const [showReplaceW9, setShowReplaceW9] = useState<boolean>(false);
  const [selectedW9File, setSelectedW9File] = useState<File | null>(null);

  // W-9 invoice-sync status (ported from 0f429e6, uses tax1099.ts directly)
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [syncedAt, setSyncedAt] = useState<string>('');
  const [syncUpdatedAt, setSyncUpdatedAt] = useState<string>('');
  const [syncError, setSyncError] = useState<string>('');
  const [eligibleCount, setEligibleCount] = useState<number | null>(null);
  const [syncResending, setSyncResending] = useState<boolean>(false);
  const [syncPollEpoch, setSyncPollEpoch] = useState<number>(0);

  useEffect(() => {
    if (!branchId) {
      return;
    }
    const settings = isConfigured ? payerData : null;
    const defaults = !isConfigured ? payerData : null;
    if (keepFormOnRefresh.current) {
      keepFormOnRefresh.current = false;
      if (settings) {
        setExisting(true);
        setMaskedEin(settings.payer_masked_ein || '');
        setW9FileId(settings.w9_file_id ?? null);
        setW9FileName(settings.w9_file_name || '');
      }
      return;
    }
    if (settings) {
      setExisting(true);
      setPrefilled(false);
      setMaskedEin(settings.payer_masked_ein || '');
      setW9FileId(settings.w9_file_id ?? null);
      setW9FileName(settings.w9_file_name || '');
      setValues({
        payer_legal_name: settings.payer_legal_name || '',
        payer_dba_name: settings.payer_dba_name || '',
        payer_tin_type: String(settings.payer_tin_type ?? '1'),
        payer_ein: '',
        address_line1: settings.address_line1 || '',
        address_line2: settings.address_line2 || '',
        city: settings.city || '',
        state: settings.state || '',
        postal_code: settings.postal_code || '',
        phone: settings.phone || '',
        transmitter_control_code: settings.transmitter_control_code || '',
        apportion_unallocated_income_to_home_state:
          Boolean(settings.apportion_unallocated_income_to_home_state),
      });
      setStatePayerIds(
        Object.entries(settings.state_payer_ids || {}).map(([code, id]) => ({
          code, id: String(id),
        })),
      );
    } else if (defaults) {
      setExisting(false);
      setPrefilled(true);
      setMaskedEin('');
      setW9FileId(null);
      setW9FileName('');
      setValues({
        ...EMPTY_FORM,
        payer_legal_name: defaults.payer_legal_name || '',
        payer_ein: defaults.payer_ein || '',
        address_line1: defaults.address_line1 || '',
        address_line2: defaults.address_line2 || '',
        city: defaults.city || '',
        state: defaults.state || '',
        postal_code: defaults.postal_code || '',
      });
      setStatePayerIds([]);
    } else {
      setExisting(false);
      setPrefilled(false);
      setMaskedEin('');
      setW9FileId(null);
      setW9FileName('');
      setValues(EMPTY_FORM);
      setStatePayerIds([]);
    }
  }, [branchId, payerData, isConfigured]);

  useEffect(() => {
    if (!branchId) {
      return;
    }
    const numericBranchId = Number(branchId);
    if (!Number.isFinite(numericBranchId) || numericBranchId <= 0) {
      return;
    }
    const loadSyncState = async () => {
      try {
        const settings = await getPayerSettings(numericBranchId);
        applySyncState(settings);
        setSyncPollEpoch(Date.now());
      } catch {
        setSyncStatus(null);
      }
      try {
        const count = await getEligibleCount(numericBranchId);
        setEligibleCount(count.withEmail);
      } catch {
        setEligibleCount(null);
      }
    };
    loadSyncState();
  }, [branchId]);

  useEffect(() => {
    if (!branchId || syncStatus !== 'PENDING') {
      return undefined;
    }
    const numericBranchId = Number(branchId);
    if (!Number.isFinite(numericBranchId) || numericBranchId <= 0) {
      return undefined;
    }
    const pollSyncState = async () => {
      try {
        const settings = await getPayerSettings(numericBranchId);
        applySyncState(settings);
      } catch {
        setSyncPollEpoch(Date.now());
      }
    };
    let polls = 0;
    const timer = setInterval(() => {
      polls += 1;
      if (polls > MAX_POLLS) {
        clearInterval(timer);
        return;
      }
      pollSyncState();
    }, POLL_INTERVAL_MS);
    return () => {
      clearInterval(timer);
      polls = 0;
    };
  }, [branchId, syncStatus, syncPollEpoch]);

  function applySyncState(settings: PayerW9Sync) {
    setSyncStatus(settings.status);
    setSyncedAt(settings.syncedAt);
    setSyncUpdatedAt(settings.syncUpdatedAt);
    setSyncError(settings.syncError);
  }

  const setField = (field: keyof PayerFormValues, value: any) => {
    setValues((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => {
      const next = { ...previous };
      delete next[field as string];
      return next;
    });
  };

  const addStateRow = () => setStatePayerIds((previous) => [...previous, { code: '', id: '' }]);
  const removeStateRow = (index: number) =>
    setStatePayerIds((previous) => previous.filter((_row, i) => i !== index));
  const setStateRow = (index: number, field: keyof StatePayerId, value: string) =>
    setStatePayerIds((previous) => previous.map((row, i) => (
      i === index ? { ...row, [field]: value } : row
    )));

  const validate = (): boolean => {
    const found: Record<string, string> = {};
    if (!values.payer_legal_name.trim()) {
      found.payer_legal_name = t('errors.legal_name_required');
    }
    if (!existing && values.payer_ein.replace(/\D/g, '').length !== 9) {
      found.payer_ein = t('errors.ein_required');
    }
    if (values.payer_ein && values.payer_ein.replace(/\D/g, '').length !== 9) {
      found.payer_ein = t('errors.ein_invalid');
    }
    if (!values.address_line1.trim()) {
      found.address_line1 = t('errors.address_required');
    }
    if (!values.city.trim()) {
      found.city = t('errors.city_required');
    }
    if (!values.state.trim()) {
      found.state = t('errors.state_required');
    }
    if (!values.postal_code.trim()) {
      found.postal_code = t('errors.postal_code_required');
    }
    statePayerIds.forEach((row, index) => {
      if (row.code && row.code.length !== 2) {
        found[`state_payer_${index}`] = t('errors.state_code_two_letters');
      }
    });
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!branchId || !validate()) {
      return;
    }

    const payload: Record<string, any> = {
      branch_id: branchId,
      payer_legal_name: values.payer_legal_name.trim(),
      payer_tin_type: Number(values.payer_tin_type),
      address_line1: values.address_line1.trim(),
      city: values.city.trim(),
      state: values.state.trim(),
      postal_code: values.postal_code.trim(),
      apportion_unallocated_income_to_home_state:
        values.apportion_unallocated_income_to_home_state,
    };

    // Retyping the EIN is the only way to change it; leaving it blank on an
    // edit keeps whatever gl-api already has, since the API never echoes it
    // back for this form to resubmit unchanged.
    if (values.payer_ein) {
      payload.payer_ein = values.payer_ein;
    }

    // The W-9 is uploaded before this record exists, so the link is made here.
    // Always sent, not just on the first save: uploading a REPLACEMENT changes
    // this id on an existing record, and withholding it left the saved row
    // pointing at the previous document while the screen showed the new one.
    // Resending an unchanged id is a no-op -- gl-api revalidates it against
    // the branch and writes the same value.
    if (w9FileId) {
      payload.w9_file_id = w9FileId;
    }

    const optionalText: Array<keyof PayerFormValues> = [
      'payer_dba_name', 'address_line2', 'phone', 'transmitter_control_code',
    ];
    optionalText.forEach((field) => {
      const value = String(values[field] ?? '').trim();
      if (value) {
        payload[field] = value;
      }
    });

    const stateIds: Record<string, string> = {};
    statePayerIds.forEach((row) => {
      if (row.code && row.id) {
        stateIds[row.code.toUpperCase()] = row.id;
      }
    });
    if (Object.keys(stateIds).length > 0) {
      payload.state_payer_ids = stateIds;
    }

    setSaving(true);
    savePayerSettings(
      payload,
      (message: string, response: any) => {
        setSaving(false);
        setExisting(true);
        setPrefilled(false);
        const settings = response?.data?.data ?? {};
        setMaskedEin(settings.payer_masked_ein || maskedEin);
        setField('payer_ein', '');
        if (settings.w9_file_id !== undefined) {
          setW9FileId(settings.w9_file_id ?? null);
          setW9FileName(settings.w9_file_name || '');
        }
        toast.success(message && !message.startsWith('tax_1099.') ? message : t('messages.saved'));
        refreshPayerData();
        const numericBranchId = Number(branchId);
        if (Number.isFinite(numericBranchId) && numericBranchId > 0) {
          getPayerSettings(numericBranchId).then((syncState) => {
            applySyncState(syncState);
            setSyncPollEpoch(Date.now());
          }).catch(() => {});
        }
      },
      (message: string, errorData: any) => {
        setSaving(false);
        const serverErrors: Record<string, string> = {};
        if (errorData?.detail && Array.isArray(errorData.detail)) {
          errorData.detail.forEach((item: any) => {
            const field = item?.loc?.[item.loc.length - 1];
            if (field) {
              serverErrors[field] = item.msg || t('errors.invalid_value', 'Invalid value');
            }
          });
        } else if (errorData?.error && typeof errorData.error === 'object' && !Array.isArray(errorData.error)) {
          Object.entries(errorData.error).forEach(([k, v]) => {
            serverErrors[k] = typeof v === 'string' ? v : JSON.stringify(v);
          });
        }
        if (Object.keys(serverErrors).length > 0) {
          setErrors((previous) => ({ ...previous, ...serverErrors }));
        }
        toast.error(message || t('errors.save_failed'));
      },
    );
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        toast.error(t('w9.select_pdf', 'Please select a valid PDF file'));
        event.target.value = '';
        setSelectedW9File(null);
        return;
      }
      setSelectedW9File(file);
    }
  };

  const handleUploadW9 = () => {
    if (!branchId || !selectedW9File) return;
    setW9Uploading(true);
    const formData = new FormData();
    formData.append('file', selectedW9File);

    uploadPayerW9(
      { branch_id: branchId, formData },
      (message: string, response: any) => {
        setW9Uploading(false);
        const data = response?.data?.data;
        if (data) {
          setW9FileId(data.w9_file_id ?? null);
          setW9FileName(data.w9_file_name || selectedW9File.name);
        }
        setSelectedW9File(null);
        setShowReplaceW9(false);
        keepFormOnRefresh.current = true;
        refreshPayerData();

        // The W-9 states the legal name, EIN and address, so fill them in for
        // review rather than making the user retype what the form already says.
        // Only blanks are filled: anything already typed is the user's, and the
        // EIN is always blank here because the API never echoes it back.
        const extracted = data?.extracted;
        const filled: string[] = [];
        if (extracted) {
          setValues((current) => {
            const next = { ...current };
            const map: Array<[keyof PayerFormValues, string]> = [
              ['payer_legal_name', t('w9.prefilled_fields.payer_legal_name', 'legal name')],
              ['payer_dba_name', t('w9.prefilled_fields.payer_dba_name', 'DBA name')],
              ['payer_ein', t('w9.prefilled_fields.payer_ein', 'EIN')],
              ['address_line1', t('w9.prefilled_fields.address_line1', 'address')],
              ['address_line2', t('w9.prefilled_fields.address_line2', 'address line 2')],
              ['city', t('w9.prefilled_fields.city', 'city')],
              ['state', t('w9.prefilled_fields.state', 'state')],
              ['postal_code', t('w9.prefilled_fields.postal_code', 'ZIP')],
            ];
            map.forEach(([field, label]) => {
              const value = extracted[field];
              if (value && !String(next[field] ?? '').trim()) {
                (next as any)[field] = String(value);
                filled.push(label);
              }
            });
            if (extracted.payer_tin_type && !existing) {
              next.payer_tin_type = String(extracted.payer_tin_type);
            }
            return next;
          });
        }

        toast.success(message && !message.startsWith('tax_1099.') ? message : t('w9.upload_success', 'Company W-9 uploaded successfully'));
        if (filled.length > 0) {
          toast.success(
            t('w9.prefilled_from_form', 'Filled in from the W-9: {{fields}}. Review, then save.', {
              fields: filled.join(', '),
            })
          );
        }
      },
      (err: any) => {
        setW9Uploading(false);
        toast.error(err?.response?.data?.message || t('w9.upload_failed', 'Failed to upload Company W-9'));
      }
    );
  };

  const handleDownloadW9Pdf = () => {
    if (!branchId) return;
    setW9Downloading(true);
    downloadPayerW9Pdf(
      // w9FileId is set by the upload and by the settings load, so this covers
      // a freshly uploaded file and a saved one alike.
      { branch_id: branchId, file_id: w9FileId ?? undefined },
      (_message: string, blob: Blob) => {
        setW9Downloading(false);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      },
      (err: any) => {
        setW9Downloading(false);
        toast.error(err?.response?.data?.message || t('w9.download_failed', 'Failed to download Company W-9 PDF'));
      }
    );
  };

  const handleResendW9 = () => {
    if (!branchId || syncResending) {
      return;
    }
    const numericBranchId = Number(branchId);
    if (!Number.isFinite(numericBranchId) || numericBranchId <= 0) {
      return;
    }
    setSyncResending(true);
    const runResend = async () => {
      try {
        const status = await resendW9(numericBranchId);
        const settings = await getPayerSettings(numericBranchId);
        applySyncState(settings);
        if (status === 'FAILED') {
          toast.error(settings.syncError || t('w9.resend_failed', 'Retry failed.'));
        } else if (status === 'SKIPPED') {
          toast(t('w9.resend_skipped', 'Invoices is not set up for this company, so nothing was sent.'));
        } else {
          toast.success(t('w9.resend_queued', 'Retry queued. The status updates when Invoices responds.'));
        }
        setSyncPollEpoch(Date.now());
      } catch (err: unknown) {
        if (err instanceof Error) {
          toast.error(err.message);
        } else {
          toast.error(t('w9.resend_failed', 'Retry failed.'));
        }
      } finally {
        setSyncResending(false);
      }
    };
    runResend();
  };

  const syncTime = syncTimeLabel(syncStatus, syncedAt, syncUpdatedAt);
  const formatSyncTime = (value: string) =>
    moment.utc(value).local().locale(i18n.language).format('Do MMM YYYY, h:mm a');

  if (!branchId) {
    return (
      <div className="p-4">
        <h4>{t('page.title')}</h4>
        <p className="text-muted">{t('messages.select_branch')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4">
        <h4>{t('page.title')}</h4>
        <p className="text-muted">{t('messages.loading')}</p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <h4 className="mb-0">{t('page.title')}</h4>
      <small className="text-muted d-block mb-3">{t('page.subtitle')}</small>

      {!existing && prefilled && (
        <div className="alert alert-info py-2">{t('messages.prefilled')}</div>
      )}
      {!existing && !prefilled && (
        <div className="alert alert-info py-2">{t('messages.not_configured')}</div>
      )}

      {/* COMPANY W-9 CARD */}
      <div
        className="card mb-4 p-3 border shadow-sm"
        style={{
          maxWidth: 720,
          borderRadius: '12px',
          borderColor: w9FileId ? '#bbf7d0' : '#dfe7f0',
          backgroundColor: w9FileId ? '#f0fdf4' : '#fff',
        }}
      >
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="mb-1 text-dark fw-bold d-flex align-items-center">
              <i className="fa fa-file-text-o me-2 text-primary" />
              {t('w9.title', 'Company Form W-9 (for your Customers & Clients)')}
            </h6>
            <small className="text-muted d-block">
              {t(
                'w9.subtitle',
                "Upload your company's signed Form W-9 PDF and the legal name, EIN and address below are filled in from it for review. When clients or customers request your W-9 for their 1099 reporting, you can download or provide it here."
              )}
            </small>
          </div>
          {w9FileId && (
            <span
              className="badge"
              style={{
                backgroundColor: '#dcfce7',
                color: '#15803d',
                borderRadius: '999px',
                padding: '5px 10px',
                fontSize: '12px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              ✓ {t('w9.on_file', 'On File')}
            </span>
          )}
        </div>

        {eligibleCount !== null && (
          <p data-testid="w9-count-notice" className="text-muted small mb-2">
            {t('w9.email_notice', {
              count: eligibleCount,
              defaultValue_one: 'W-9 will be emailed to {{count}} active 1099 customer with an email address in Invoices.',
              defaultValue_other: 'W-9 will be emailed to {{count}} active 1099 customers with an email address in Invoices.',
            })}
          </p>
        )}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <span className="text-muted small">{t('w9.sync_title', 'Invoice sync')}</span>
          {syncStatus !== null ? (
            <span
              data-testid="w9-sync-badge"
              style={{
                ...BADGE_STYLE[syncStatus],
                padding: '2px 10px',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: '12px',
              }}
            >
              {t(`w9.sync_status.${syncStatus}`, SYNC_STATUS_LABEL[syncStatus])}
            </span>
          ) : (
            <span className="text-muted small">{t('w9.sync_not_reported', 'Sync status not reported yet.')}</span>
          )}
          {syncTime && (
            <span data-testid="w9-sync-time" className="text-muted small">
              {` · ${t(syncTime.key, { time: formatSyncTime(syncTime.time) })}`}
            </span>
          )}
          {syncStatus === 'FAILED' && syncError && (
            <span data-testid="w9-sync-error" className="text-danger small">{` · ${syncError}`}</span>
          )}
          {syncStatus === 'SKIPPED' && (
            <span data-testid="w9-sync-skip-note" className="text-muted small">
              {t('w9.sync_skipped', ' · Invoices is not set up for this company')}
            </span>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            data-testid="w9-resend-btn"
            disabled={!branchId || syncResending || syncStatus !== 'FAILED'}
            onClick={handleResendW9}
            style={{ borderRadius: '6px' }}
          >
            {syncResending ? t('w9.resending', 'Retrying...') : t('w9.resend', 'Retry sync')}
          </Button>
        </div>

        {w9FileId && !showReplaceW9 ? (
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-2 pt-2 border-top">
            <div className="d-flex align-items-center">
              <i className="fa fa-file-pdf-o text-danger fs-5 me-2" />
              <div>
                <span className="fw-semibold text-dark small d-block">
                  {w9FileName || 'Company_W9.pdf'}
                </span>
                <span className="text-muted" style={{ fontSize: '11px' }}>
                  {t('w9.stored_in_freezerdoc', 'Stored securely in FreezerDoc')}
                </span>
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                disabled={w9Downloading}
                onClick={handleDownloadW9Pdf}
                style={{ borderRadius: '6px', fontWeight: 600 }}
              >
                <i className="fa fa-download me-1" />
                {w9Downloading ? t('w9.opening', 'Opening...') : t('w9.view_download', 'View / Download W-9')}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowReplaceW9(true)}
                style={{ borderRadius: '6px' }}
              >
                <i className="fa fa-refresh me-1" />
                {t('w9.replace', 'Replace')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-2 pt-2 border-top">
            <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
              <Form.Control
                type="file"
                accept="application/pdf"
                size="sm"
                onChange={handleFileSelect}
                disabled={w9Uploading}
                style={{ maxWidth: 380 }}
              />
              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!selectedW9File || w9Uploading}
                  onClick={handleUploadW9}
                  style={{ borderRadius: '6px', fontWeight: 600 }}
                >
                  <i className="fa fa-upload me-1" />
                  {w9Uploading ? t('w9.uploading', 'Uploading...') : t('w9.upload_btn', 'Upload Signed W-9 PDF')}
                </Button>
                {w9FileId && showReplaceW9 && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setShowReplaceW9(false);
                      setSelectedW9File(null);
                    }}
                    style={{ borderRadius: '6px' }}
                  >
                    {t('w9.cancel', 'Cancel')}
                  </Button>
                )}
              </div>
            </div>
            <small className="text-muted d-block mt-1">
              {t('w9.help_text', 'Accepted format: PDF only. Maximum size: 10MB.')}
            </small>
          </div>
        )}
      </div>

      <Form onSubmit={handleSubmit} noValidate style={{ maxWidth: 720 }}>
        <div className="row">
          <Form.Group className="col-md-8 mb-3">
            <Form.Label>
              {t('form.legal_name')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              maxLength={255}
              value={values.payer_legal_name}
              isInvalid={Boolean(errors.payer_legal_name)}
              onChange={(event) => setField('payer_legal_name', event.target.value)}
            />
            <Form.Control.Feedback type="invalid">
              {errors.payer_legal_name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="col-md-4 mb-3">
            <Form.Label>{t('form.dba_name')}</Form.Label>
            <Form.Control
              type="text"
              maxLength={255}
              value={values.payer_dba_name}
              isInvalid={Boolean(errors.payer_dba_name)}
              onChange={(event) => setField('payer_dba_name', event.target.value)}
            />
            <Form.Control.Feedback type="invalid">{errors.payer_dba_name}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="col-md-4 mb-3">
            <Form.Label>{t('form.tin_type')}</Form.Label>
            <Form.Select
              value={values.payer_tin_type}
              onChange={(event) => setField('payer_tin_type', event.target.value)}
            >
              <option value="1">{t('form.tin_type_ein')}</option>
              <option value="2">{t('form.tin_type_ssn')}</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="col-md-4 mb-3">
            <Form.Label>
              {t('form.ein')} {!existing && <span className="text-danger">*</span>}
            </Form.Label>
            <Form.Control
              type="text"
              maxLength={11}
              placeholder={existing ? maskedEin : t('form.ein_placeholder')}
              value={values.payer_ein}
              isInvalid={Boolean(errors.payer_ein)}
              onChange={(event) => setField('payer_ein', event.target.value)}
            />
            <Form.Control.Feedback type="invalid">{errors.payer_ein}</Form.Control.Feedback>
            {existing && (
              <Form.Text muted>{t('form.ein_help_existing', { masked: maskedEin })}</Form.Text>
            )}
          </Form.Group>

          <Form.Group className="col-md-4 mb-3">
            <Form.Label>{t('form.phone')}</Form.Label>
            <Form.Control
              type="text"
              maxLength={64}
              value={values.phone}
              isInvalid={Boolean(errors.phone)}
              onChange={(event) => setField('phone', event.target.value)}
            />
            <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
          </Form.Group>

          <div className="col-12"><hr /></div>

          <Form.Group className="col-md-6 mb-3">
            <Form.Label>
              {t('form.address_line1')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              maxLength={255}
              value={values.address_line1}
              isInvalid={Boolean(errors.address_line1)}
              onChange={(event) => setField('address_line1', event.target.value)}
            />
            <Form.Control.Feedback type="invalid">
              {errors.address_line1}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="col-md-6 mb-3">
            <Form.Label>{t('form.address_line2')}</Form.Label>
            <Form.Control
              type="text"
              maxLength={255}
              value={values.address_line2}
              isInvalid={Boolean(errors.address_line2)}
              onChange={(event) => setField('address_line2', event.target.value)}
            />
            <Form.Control.Feedback type="invalid">{errors.address_line2}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="col-md-4 mb-3">
            <Form.Label>
              {t('form.city')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              maxLength={100}
              value={values.city}
              isInvalid={Boolean(errors.city)}
              onChange={(event) => setField('city', event.target.value)}
            />
            <Form.Control.Feedback type="invalid">{errors.city}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="col-md-4 mb-3">
            <Form.Label>
              {t('form.state')} <span className="text-danger">*</span>
            </Form.Label>
            <Select
              classNamePrefix="react-select"
              placeholder={t('form.select_state', 'Select state')}
              isClearable
              options={STATE_OPTIONS}
              value={
                STATE_OPTIONS.find((option) => option.value === values.state) ||
                (values.state ? { value: values.state, label: values.state } : null)
              }
              onChange={(selected) => setField('state', selected ? selected.value : '')}
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: errors.state ? '#dc3545' : base.borderColor,
                  boxShadow: state.isFocused && errors.state ? '0 0 0 0.25rem rgba(220,53,69,.25)' : base.boxShadow,
                }),
              }}
            />
            {errors.state && <div className="invalid-feedback d-block">{errors.state}</div>}
          </Form.Group>

          <Form.Group className="col-md-4 mb-3">
            <Form.Label>
              {t('form.postal_code')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              maxLength={20}
              value={values.postal_code}
              isInvalid={Boolean(errors.postal_code)}
              onChange={(event) => setField('postal_code', event.target.value)}
            />
            <Form.Control.Feedback type="invalid">
              {errors.postal_code}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="col-12"><hr /></div>

          <Form.Group className="col-md-6 mb-3">
            <Form.Label>{t('form.transmitter_control_code')}</Form.Label>
            <Form.Control
              type="text"
              maxLength={20}
              value={values.transmitter_control_code}
              isInvalid={Boolean(errors.transmitter_control_code)}
              onChange={(event) => setField('transmitter_control_code', event.target.value)}
            />
            <Form.Control.Feedback type="invalid">{errors.transmitter_control_code}</Form.Control.Feedback>
            <Form.Text muted>{t('form.transmitter_control_code_help')}</Form.Text>
          </Form.Group>

          <Form.Group className="col-md-6 mb-3 d-flex align-items-center">
            <Form.Check
              type="checkbox"
              id="apportion_unallocated_income"
              label={t('form.apportion')}
              checked={values.apportion_unallocated_income_to_home_state}
              onChange={(event) =>
                setField('apportion_unallocated_income_to_home_state', event.target.checked)
              }
            />
          </Form.Group>
          <Form.Text muted className="col-12 mb-3">{t('form.apportion_help')}</Form.Text>

          <div className="col-12">
            <Form.Label>{t('form.state_payer_ids')}</Form.Label>
            <p className="text-muted small">{t('form.state_payer_ids_help')}</p>
            {statePayerIds.map((row, index) => (
              <div className="row mb-2 align-items-center" key={index}>
                <div className="col-md-4 col-12 mb-1 mb-md-0">
                  <Select
                    classNamePrefix="react-select"
                    placeholder={t('form.select_state', 'Select state')}
                    isClearable
                    options={STATE_OPTIONS}
                    value={
                      STATE_OPTIONS.find((option) => option.value === row.code) ||
                      (row.code ? { value: row.code, label: row.code } : null)
                    }
                    onChange={(selected) => setStateRow(index, 'code', selected ? selected.value : '')}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderColor: errors[`state_payer_${index}`] ? '#dc3545' : base.borderColor,
                        boxShadow: state.isFocused && errors[`state_payer_${index}`] ? '0 0 0 0.25rem rgba(220,53,69,.25)' : base.boxShadow,
                      }),
                    }}
                  />
                </div>
                <div className="col-md-5 col-8">
                  <Form.Control
                    type="text"
                    maxLength={50}
                    placeholder={t('form.state_registration_id')}
                    value={row.id}
                    onChange={(event) => setStateRow(index, 'id', event.target.value)}
                  />
                </div>
                <div className="col-md-3 col-4">
                  <Button variant="outline-danger" size="sm" onClick={() => removeStateRow(index)}>
                    {t('form.remove')}
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="outline-secondary" size="sm" onClick={addStateRow}>
              {t('form.add_state')}
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? t('form.saving') : t('form.save')}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default Tax1099PayerSettings;
